import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, FileDown, FileText } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { useLightbox } from "../components/Lightbox";

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { title: "Certificate of Analysis (COA)", copy: "Batch-level analysis confirming the agreed product specification." },
  { title: "Product Specification Sheet", copy: "The confirmed technical specification for the ordered product configuration." },
  { title: "MSDS / SDS", copy: "Material safety documentation, where applicable to the product." },
  { title: "Commercial Invoice", copy: "Issued per order as part of the export documentation set." },
  { title: "Packing List", copy: "Container-level packing details issued with each shipment." },
  { title: "Export Documentation", copy: "Standard Indonesian export documents and other destination-specific documents where applicable." },
];

const PRODUCT_DOCS = [
  { slug: "coconut-shell-shisha-charcoal", name: "Coconut Shell Shisha Charcoal Briquettes" },
  { slug: "bbq-coconut-charcoal", name: "BBQ Coconut Charcoal Briquettes" },
];

const LAB_CERTIFICATES = [
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_e7859763-7706-490d-8538-ac5bbbfd91cf/artifacts/furoh15e_cert-roa.webp",
    caption: "Report of Analysis (ROA) — Proximate",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_e7859763-7706-490d-8538-ac5bbbfd91cf/artifacts/d51bay9k_cert-sht-page1.webp",
    caption: "Self-Heating Test (SHT) — 1/2",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_e7859763-7706-490d-8538-ac5bbbfd91cf/artifacts/xobuod2z_cert-sht-page2.webp",
    caption: "Self-Heating Test (SHT) — 2/2, Result: Negative",
  },
  {
    src: "https://customer-assets-v7afamib.emergentagent.net/job_e7859763-7706-490d-8538-ac5bbbfd91cf/artifacts/bpkebhte_cert-msds-page1.webp",
    caption: "Material Safety Data Sheet (MSDS)",
  },
];

function LabCertificationSection() {
  const openLightbox = useLightbox();
  const images = LAB_CERTIFICATES.map((c) => ({ src: c.src, alt: c.caption, caption: c.caption, noWatermark: true }));
  return (
    <Reveal className="mt-20">
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Independent Lab Certification</h2>
      <p className="mt-3 text-sm text-offwhite/55 max-w-2xl leading-relaxed">
        Tested by PT Carsurin, a KAN-accredited ISO/IEC 17025 laboratory. Latest analysis: September 2026. Fresh
        SHT and COA are issued per shipment.
      </p>
      <Stagger
        data-testid="lab-certification-grid"
        className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
        gap={0.07}
      >
        {LAB_CERTIFICATES.map((cert, i) => (
          <StaggerItem key={cert.src}>
            <div
              data-testid={`lab-cert-${i + 1}`}
              onClick={() => openLightbox(images, i)}
              className="group bg-charcoal hover:bg-white/[0.04] p-3 sm:p-4 flex flex-col gap-3 h-full transition-colors duration-300 cursor-zoom-in"
            >
              <div className="relative aspect-[3/4] overflow-hidden border border-white/10">
                <img
                  src={cert.src}
                  alt={cert.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <p className="text-xs font-medium text-offwhite/70 leading-snug">{cert.caption}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <p className="mt-4 text-xs text-offwhite/40 italic">Full documents are provided to buyers upon request.</p>
    </Reveal>
  );
}

function ProductDocList({ slug, name }) {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/products/${slug}/documents`)
      .then((res) => setDocs(res.data.documents))
      .catch(() => setDocs([]));
  }, [slug]);

  return (
    <div data-testid={`docs-list-${slug}`} className="border border-white/10">
      <div className="px-6 sm:px-8 py-5 border-b border-white/10 bg-white/[0.03]">
        <h3 className="font-display text-lg font-bold tracking-tight">{name}</h3>
      </div>
      {docs === null ? null : docs.length > 0 ? (
        docs.map((doc) => (
          <a
            key={doc.id}
            href={`${API}/api/documents/${doc.id}/download`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`docs-download-${doc.id}`}
            className="group flex items-center justify-between gap-4 px-6 sm:px-8 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.05] transition-colors duration-300"
          >
            <span className="flex items-center gap-4">
              <FileText className="w-4 h-4 text-clay shrink-0" />
              <span className="text-sm font-medium text-offwhite">{doc.label}</span>
            </span>
            <FileDown className="w-4 h-4 text-offwhite/50 group-hover:text-clay transition-colors duration-300" />
          </a>
        ))
      ) : (
        <p className="px-6 sm:px-8 py-5 text-sm text-offwhite/50 italic">Documentation available upon request.</p>
      )}
    </div>
  );
}

export default function Documentation() {
  return (
    <>
      <SEO />
      <div data-testid="documentation-page" className="bg-charcoal text-offwhite min-h-screen">
        <section className="pt-40 pb-16 sm:pb-20 tech-grid">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="DOCS" title="Product Documentation" dark />
            <Reveal className="mt-8 max-w-4xl">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
                Product Documentation
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-offwhite/60 leading-relaxed">
                Documentation availability depends on the product and manufacturing partner. Only verified documents
                are shared with buyers — we do not publish templates or unverified certificates.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-20 sm:pb-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" gap={0.07}>
              {DOC_TYPES.map((doc) => (
                <StaggerItem key={doc.title}>
                  <div
                    data-testid={`doc-type-${doc.title.toLowerCase().replace(/[\s/()]+/g, "-")}`}
                    className="bg-charcoal hover:bg-white/[0.04] p-8 h-full flex flex-col gap-4 transition-colors duration-300"
                  >
                    <FileText className="w-6 h-6 text-clay" strokeWidth={1.5} />
                    <h2 className="font-display text-lg font-bold tracking-tight">{doc.title}</h2>
                    <p className="text-sm text-offwhite/55 leading-relaxed">{doc.copy}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>

            <Reveal className="mt-20">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Available Downloads</h2>
              <p className="mt-3 text-sm text-offwhite/55 max-w-xl leading-relaxed">
                Verified product documents published for buyer review appear below. Where no document is listed,
                documentation is available upon request.
              </p>
            </Reveal>
            <div className="mt-10 grid lg:grid-cols-2 gap-8">
              {PRODUCT_DOCS.map((p) => (
                <Reveal key={p.slug}>
                  <ProductDocList slug={p.slug} name={p.name} />
                </Reveal>
              ))}
            </div>

            <LabCertificationSection />

            <Reveal className="mt-20 border border-white/10 p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight">Need a specific document?</h2>
                <p className="mt-3 text-sm text-offwhite/60 max-w-lg leading-relaxed">
                  Request the COA, specification sheet or MSDS for your product during the quotation process.
                </p>
              </div>
              <Link
                to="/request-a-quote"
                data-testid="docs-quote-cta"
                className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300 shrink-0"
              >
                Request Documentation
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
